// src/app/job-details.jsx
import React, { useEffect, useState } from 'react';
import {
  View,
  Text,
  TextInput,
  StyleSheet,
  ScrollView,
  TouchableOpacity,
  KeyboardAvoidingView,
  Platform,
} from 'react-native';
import axios from 'axios';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { format } from 'date-fns';
import API_BASE_URL from '../apiConfig.js';

// token helper
const getToken = () => {
  try { return localStorage.getItem('userToken'); } catch { return null; }
};

// helper to ensure HH:mm:ss for API
const withSeconds = (hhmm = '') => (hhmm && hhmm.length === 5 ? `${hhmm}:00` : hhmm || '');

// Helper to convert UTC date/time to local date/time for editing
const utcToLocal = (dateStr, timeStr) => {
  if (!dateStr || !timeStr) return { localDate: '', localTime: '' };
  
  // Create UTC date from the stored values
  const utcDate = new Date(`${dateStr}T${timeStr}Z`);
  
  // Convert to local date and time strings for the inputs
  const localDate = utcDate.getFullYear() + '-' + 
    String(utcDate.getMonth() + 1).padStart(2, '0') + '-' + 
    String(utcDate.getDate()).padStart(2, '0');
  
  const localTime = String(utcDate.getHours()).padStart(2, '0') + ':' + 
    String(utcDate.getMinutes()).padStart(2, '0');
  
  return { localDate, localTime };
};

// Helper to convert local date/time back to UTC for saving
const localToUtc = (localDate, localTime) => {
  if (!localDate || !localTime) return { utcDate: '', utcTime: '' };
  
  // Create local date from the input values
  const localDateTime = new Date(`${localDate}T${localTime}`);
  
  // Convert to UTC date and time strings for the API
  const utcDate = localDateTime.getUTCFullYear() + '-' + 
    String(localDateTime.getUTCMonth() + 1).padStart(2, '0') + '-' + 
    String(localDateTime.getUTCDate()).padStart(2, '0');
  
  const utcTime = String(localDateTime.getUTCHours()).padStart(2, '0') + ':' + 
    String(localDateTime.getUTCMinutes()).padStart(2, '0') + ':00';
  
  return { utcDate, utcTime };
};

export default function JobDetails() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const id = searchParams.get('id');

  const [job, setJob] = useState(null);
  const [isEditing, setIsEditing] = useState(false);
  const [editedJob, setEditedJob] = useState(null);
  const [saving, setSaving] = useState(false);
  const [userPermissions, setUserPermissions] = useState(null);

  // Load user permissions on component mount and check access
  useEffect(() => {
    const loadUserPermissions = async () => {
      try {
        const permissions = localStorage.getItem('userPermissions');
        if (permissions) {
          const parsedPermissions = JSON.parse(permissions);
          setUserPermissions(parsedPermissions);
          
          // Check if user has job permissions
          if (parsedPermissions?.job_perm === 'none') {
            alert("You don't have permission to view jobs. Please contact your account administrator.");
            navigate('/'); // Redirect to home page
            return;
          }
        }
      } catch (error) {
        console.error('Error loading user permissions:', error);
      }
    };
    loadUserPermissions();
  }, [navigate]);

  // Check if user has job write permissions
  const hasJobWritePermission = () => {
    return userPermissions?.job_perm === 'write';
  };

  // Check if user can see prices
  const canSeePrices = () => {
    return userPermissions?.price_perm === 'visible';
  };

  const loadJob = async () => {
    if (!id) return;
    try {
      const token = getToken();
      const { data } = await axios.get(`${API_BASE_URL}/jobs/${encodeURIComponent(id)}`, {
        headers: token ? { Authorization: `Bearer ${token}` } : undefined,
      });
      const normalized = {
        id: data.id,
        visitType: data.visitType, // 'job' | 'quote'
        customerId: data.customerId,
        customerName: data.customerName || 'Unknown Customer',
        title: data.title || '',
        description: data.description || '',
        price: data.price ?? '',
        selectedDate: data.selectedDate || '', // YYYY-MM-DD
        selectedTime: data.selectedTime || '', // HH:mm:ss
        notes: data.notes || '',
      };
      // keep price as string for TextInput
      normalized.price = String(normalized.price ?? '');
      setJob(normalized);
      setEditedJob(normalized);
    } catch (err) {
      console.error('Error fetching job:', err);
      alert('Failed to load job. Please try again later.');
    }
  };

  useEffect(() => {
    loadJob();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const saveEdits = async (extra = {}) => {
    if (!job || !editedJob) return;

    // Convert local date/time back to UTC for saving
    const { utcDate, utcTime } = localToUtc(editedJob.selectedDate, editedJob.selectedTime?.slice(0, 5));

    // Build payload expected by backend
    const payload = {
      customerName: job.customerName,
      title: editedJob.title,
      description: editedJob.description,
      price: editedJob.price ? parseFloat(editedJob.price) : 0,
      selectedDate: utcDate || editedJob.selectedDate, // Use UTC date
      selectedTime: utcTime || withSeconds((editedJob.selectedTime || '').slice(0, 5)), // Use UTC time
      notes: editedJob.notes,
      ...extra,
    };

    // strip undefined/null
    Object.keys(payload).forEach((k) => {
      if (payload[k] == null || payload[k] === '') delete payload[k];
    });

    try {
      setSaving(true);
      const token = getToken();
      await axios.put(`${API_BASE_URL}/jobs/edit/${encodeURIComponent(job.id)}`, payload, {
        headers: token ? { Authorization: `Bearer ${token}` } : undefined,
      });
      setJob((prev) => ({ ...prev, ...payload, visitType: payload.visitType ?? prev.visitType }));
      setIsEditing(false);
    } catch (err) {
      console.error('Error saving job edits:', err);
      alert('Failed to save job edits. Please try again later.');
    } finally {
      setSaving(false);
    }
  };

  const deleteJob = async () => {
    if (!job) return;
    const ok = window.confirm('Are you sure you want to delete this job?');
    if (!ok) return;

    try {
      const token = getToken();
      await axios.delete(`${API_BASE_URL}/jobs/${encodeURIComponent(job.id)}`, {
        headers: token ? { Authorization: `Bearer ${token}` } : undefined,
      });
      navigate('/jobs');
    } catch (err) {
      console.error('Error deleting job:', err);
      alert('Failed to delete job. Please try again later.');
    }
  };

  const prettyLocal = (dateStr, timeStr) => {
    if (!dateStr || !timeStr) return '';
    const d = new Date(`${dateStr}T${timeStr}Z`);
    return format(d, 'MMMM dd, yyyy hh:mm a');
  };

  // Helper to format local date/time for edit mode preview (no UTC conversion needed)
  const prettyLocalEdit = (dateStr, timeStr) => {
    if (!dateStr || !timeStr) return '';
    const d = new Date(`${dateStr}T${timeStr}`);
    return format(d, 'MMMM dd, yyyy hh:mm a');
  };

  if (!id) {
    return (
      <View style={[styles.container, { justifyContent: 'center', alignItems: 'center' }]}>
        <Text>No job id provided.</Text>
      </View>
    );
  }

  if (!job || !editedJob) {
    return (
      <View style={[styles.container, { justifyContent: 'center', alignItems: 'center' }]}>
        <Text>Loading…</Text>
      </View>
    );
  }

  return (
    <KeyboardAvoidingView
      style={{ minHeight: '100vh', backgroundColor: '#f0f8ff' }}
      behavior={Platform.OS === 'ios' ? 'padding' : undefined}
    >
      <ScrollView contentContainerStyle={styles.container} keyboardShouldPersistTaps="handled">
        <Text style={[styles.header, { fontSize: 14, color: '#007AFF', textDecorationLine: 'underline', textAlign: 'right' }]}>
          {job.visitType === 'job' ? `Job #${job.id}` : `Quote #${job.id}`}
        </Text>

        {/* Customer */}
        <Text style={[styles.label, { marginTop: 0 }]}>Customer</Text>
        <Text style={styles.value}>{job.customerName}</Text>

        {/* Title */}
        <Text style={styles.label}>Job Title</Text>
        {isEditing ? (
          <TextInput
            style={styles.input}
            value={editedJob.title}
            onChangeText={(t) => setEditedJob((p) => ({ ...p, title: t }))}
          />
        ) : (
          <Text style={styles.value}>{job.title}</Text>
        )}

        {/* Description */}
        <Text style={styles.label}>Description</Text>
        {isEditing ? (
          <TextInput
            style={[styles.input, { height: 80 }]}
            multiline
            value={editedJob.description}
            onChangeText={(t) => setEditedJob((p) => ({ ...p, description: t }))}
          />
        ) : (
          <Text style={styles.value}>{job.description}</Text>
        )}

        {/* Price (only for visitType=job) */}
        {job.visitType === 'job' && canSeePrices() && (
          <>
            <Text style={styles.label}>Price</Text>
            {isEditing ? (
              <TextInput
                style={styles.input}
                keyboardType="numeric"
                value={editedJob.price}
                onChangeText={(t) => setEditedJob((p) => ({ ...p, price: t }))}
              />
            ) : (
              <Text style={styles.value}>
                <Text style={{ fontWeight: 'bold' }}>$</Text>
                {Number(job.price || 0).toLocaleString()}
              </Text>
            )}
          </>
        )}

        {/* Date / Time */}
        <Text style={styles.label}>Date</Text>
        {isEditing ? (
          <View style={{ gap: 8 }}>
            <View style={{ flexDirection: 'row', gap: 8, marginTop: 5 }}>
              <input
                type="date"
                value={editedJob.selectedDate || ''}
                onChange={(e) => setEditedJob((p) => ({ ...p, selectedDate: e.target.value }))}
                style={htmlInputStyle}
              />
              <input
                type="time"
                value={(editedJob.selectedTime || '').slice(0, 5)}
                onChange={(e) => {
                  const v = e.target.value; // HH:mm
                  setEditedJob((p) => ({ ...p, selectedTime: withSeconds(v) }));
                }}
                style={htmlInputStyle}
              />
            </View>
            <View style={{ marginTop: 6 }}>
              <Text style={{ color: '#007AFF', fontWeight: 'bold' }}>
                {prettyLocalEdit(editedJob.selectedDate, editedJob.selectedTime) || 'Select a valid date and time'}
              </Text>
            </View>
          </View>
        ) : (
          <Text style={styles.value}>{prettyLocal(job.selectedDate, job.selectedTime)}</Text>
        )}

        {/* Notes */}
        <Text style={styles.label}>Notes</Text>
        {isEditing ? (
          <TextInput
            style={[styles.input, { height: 80 }]}
            multiline
            value={editedJob.notes}
            onChangeText={(t) => setEditedJob((p) => ({ ...p, notes: t }))}
          />
        ) : (
          <Text style={styles.value}>{job.notes || 'No notes available'}</Text>
        )}

        {/* Primary action buttons */}
        {hasJobWritePermission() && (
          <View style={styles.buttonRow}>
            {isEditing ? (
              <>
                <TouchableOpacity
                  style={[styles.saveButton, saving && { opacity: 0.7 }]}
                  onPress={() => saveEdits()}
                  disabled={saving}
                >
                  <Text style={styles.saveButtonText}>{saving ? 'Saving…' : 'Save'}</Text>
                </TouchableOpacity>
                <TouchableOpacity
                  style={styles.cancelButton}
                  onPress={() => {
                    setEditedJob({ ...job });
                    setIsEditing(false);
                  }}
                >
                  <Text style={styles.cancelButtonText}>Cancel</Text>
                </TouchableOpacity>
              </>
            ) : (
              <>
                <TouchableOpacity
                  style={styles.editButton}
                  onPress={() => {
                    // Convert UTC date/time to local time for editing
                    const { localDate, localTime } = utcToLocal(job.selectedDate, job.selectedTime);
                    const editJobWithLocalTime = {
                      ...job,
                      selectedDate: localDate || job.selectedDate,
                      selectedTime: localTime ? `${localTime}:00` : job.selectedTime
                    };
                    setEditedJob(editJobWithLocalTime);
                    setIsEditing(true);
                  }}
                >
                  <Text style={styles.editButtonText}>Edit</Text>
                </TouchableOpacity>
                <TouchableOpacity style={styles.deleteButton} onPress={deleteJob}>
                  <Text style={styles.deleteButtonText}>Delete</Text>
                </TouchableOpacity>
              </>
            )}
          </View>
        )}

        {/* Convert / Create Quote / Revert */}
        {hasJobWritePermission() && job.visitType === 'quote' && (
          <>
            <TouchableOpacity
              style={styles.convertButton}
              onPress={async () => {
                if (!Number(job.price || 0)) {
                  const setPrice = window.confirm('Price is $0.00. Would you like to set a price first?');
                  if (setPrice) {
                    const p = window.prompt('Enter price for the job:', '');
                    if (p && !Number.isNaN(Number(p))) {
                      await saveEdits({ visitType: 'job', price: Number(p) });
                      await loadJob();
                      return;
                    }
                  }
                }
                await saveEdits({ visitType: 'job' });
                await loadJob();
              }}
            >
              <Text style={styles.convertButtonText}>Convert to Job</Text>
            </TouchableOpacity>

            <TouchableOpacity
              style={styles.createQuoteButton}
              onPress={() => {
                const qs = new URLSearchParams({
                  id: String(job.id),
                  customerId: String(job.customerId ?? ''),
                  customerName: job.customerName ?? '',
                  title: job.title ?? '',
                  description: job.description ?? '',
                  price: String(job.price ?? ''),
                }).toString();
                navigate(`/create-quote?${qs}`);
              }}
            >
              <Text style={styles.createQuoteButtonText}>Create Quote</Text>
            </TouchableOpacity>
          </>
        )}

        {hasJobWritePermission() && job.visitType === 'job' && (
          <TouchableOpacity
            style={styles.revertButton}
            onPress={async () => {
              await saveEdits({ visitType: 'quote' });
              await loadJob();
            }}
          >
            <Text style={styles.revertButtonText}>Revert to Quote</Text>
          </TouchableOpacity>
        )}
      </ScrollView>
    </KeyboardAvoidingView>
  );
}

const htmlInputStyle = {
  padding: 10,
  border: '1px solid #ccc',
  borderRadius: 5,
  outline: 'none',
  fontSize: 14,
};

const styles = StyleSheet.create({
  container: {
    padding: 20,
    paddingBottom: 100,
    backgroundColor: '#f0f8ff',
    flex: 1,
  },
  header: { fontSize: 24, fontWeight: 'bold', color: '#333', marginBottom: 10, textAlign: 'center' },

  label: {
    fontWeight: 'bold',
    marginTop: 15,
    fontSize: 20,
    color: '#333',
    borderBottomWidth: 1,
    borderBottomColor: '#ddd',
    paddingBottom: 5,
  },
  value: { fontSize: 16, marginTop: 5, color: '#555' },

  input: {
    borderWidth: 1, borderColor: '#ccc', padding: 8, borderRadius: 4, marginTop: 5,
    backgroundColor: '#fff', shadowColor: '#000', shadowOffset: { width: 0, height: 1 },
    shadowOpacity: 0.1, shadowRadius: 2, elevation: 1,
  },

  buttonRow: { marginTop: 30, flexDirection: 'row', justifyContent: 'space-between' },
  editButton: {
    backgroundColor: '#007AFF', padding: 10, borderRadius: 5, alignItems: 'center',
    flex: 1, marginRight: 10, shadowColor: '#000', shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.2, shadowRadius: 3, elevation: 2,
  },
  editButtonText: { color: '#fff', fontSize: 16, fontWeight: 'bold' },

  deleteButton: {
    backgroundColor: '#FF3B30', padding: 10, borderRadius: 5, alignItems: 'center',
    flex: 1, shadowColor: '#000', shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.2, shadowRadius: 3, elevation: 2,
  },
  deleteButtonText: { color: '#fff', fontSize: 16, fontWeight: 'bold' },

  saveButton: {
    backgroundColor: '#34C759', padding: 10, borderRadius: 5, alignItems: 'center',
    flex: 1, shadowColor: '#000', shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.2, shadowRadius: 3, elevation: 2,
  },
  saveButtonText: { color: '#fff', fontSize: 16, fontWeight: 'bold' },

  cancelButton: {
    backgroundColor: '#FF3B30', padding: 10, borderRadius: 8, alignItems: 'center',
    marginLeft: 10, flex: 1,
  },
  cancelButtonText: { color: '#fff', fontSize: 16, fontWeight: 'bold' },

  convertButton: {
    backgroundColor: '#FF9500', padding: 10, borderRadius: 5, alignItems: 'center',
    marginTop: 20, shadowColor: '#000', shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.2, shadowRadius: 3, elevation: 2,
  },
  convertButtonText: { color: '#fff', fontSize: 16, fontWeight: 'bold' },

  revertButton: {
    backgroundColor: '#007AFF', padding: 10, borderRadius: 5, alignItems: 'center',
    marginTop: 20, shadowColor: '#000', shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.2, shadowRadius: 3, elevation: 2,
  },
  revertButtonText: { color: '#fff', fontSize: 16, fontWeight: 'bold' },

  createQuoteButton: {
    backgroundColor: '#34C759', padding: 10, borderRadius: 5, alignItems: 'center',
    marginTop: 20, shadowColor: '#000', shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.2, shadowRadius: 3, elevation: 2,
  },
  createQuoteButtonText: { color: '#fff', fontSize: 16, fontWeight: 'bold' },
});
